import { PinwheelExperienceInput } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  DD_PROMPT_MAX_ADVANCE_LIMIT,
  DD_PROMPT_MIN_DIRECT_DEPOSIT_AMOUNT,
} from 'features/advance/constants/constants';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { Tag, TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDirectDepositLoadingComponent } from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

enum DirectDepositOption {
  WITH_DD = 'with_dd',
  WITHOUT_DD = 'without_dd',
}

const DD_BORROW_MORE_PHASE_2_SCREEN_KEY = 'dd-borrow-more-phase-2';
const CTA_WITH_DD = 'Continue with direct deposit';
const CTA_WITHOUT_DD = 'Continue without direct deposit';

/**
 * Customized `DDBorrowMore`-based variant shown, on its own route, to a
 * returning/re-qualified Varo Advance customer who still needs Direct
 * Deposit set up. Reuses the same `dd-borrow-more` analytics event as
 * `DDBorrowMore` so both variants are comparable in analytics, while
 * "without DD" continues to `AdvanceAmountSelection` instead of
 * `DDBorrowMore`'s account-linking destination, since this customer is
 * already qualified and should still be able to take out the advance.
 */
const TakeOutAdvanceDdPrompt = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id?: string }>();
  const [preferredOption, setPreferredOption] = useState<DirectDepositOption>(
    DirectDepositOption.WITH_DD,
  );
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'takeOutAdvanceDdPrompt',
  });
  const dismissToTab = useDismissToRootTab();
  const goHome = () => dismissToTab(Tabs.HOME);

  const {
    shouldLaunchPinwheelSDK,
    isLoading: pinwheelFlowDecisionLoading,
    pinwheelFlowPageError,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({
    pause: false,
  });

  const { initiatePinwheel, isPinwheelLoading: isPinwheelSdkLoading } =
    usePinwheel();

  const pinwheelErrorAttribute = String(!!pinwheelFlowPageError);

  // Held back until the Pinwheel decision resolves so `pinwheel_error`
  // reports the settled value rather than an optimistic `false`.
  useViewDidFocusScreenAnalyticsEvent(
    pinwheelFlowDecisionLoading
      ? null
      : {
          key: DD_BORROW_MORE_PHASE_2_SCREEN_KEY,
          customAttributes: { pinwheel_error: pinwheelErrorAttribute },
        },
  );

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  // The Direct Deposit option is not offered when the Pinwheel decision
  // failed, so it must not stay selected either. Deriving it here keeps the
  // selection and the radios that are actually rendered in lockstep.
  const selectedOption = pinwheelFlowPageError
    ? DirectDepositOption.WITHOUT_DD
    : preferredOption;

  const navigateToAmountSelection = () => {
    router.replace({
      pathname: '/varo-advance/new',
      params: { id, skipDdPrompt: 'true' },
    });
  };

  const handleGetAdvance = () => {
    if (selectedOption === DirectDepositOption.WITHOUT_DD) {
      navigateToAmountSelection();
      return;
    }

    const outcomeCallback = shouldLaunchPinwheelSDK
      ? navigateToAmountSelection
      : goHome;

    initiatePinwheel({
      onOutcome: outcomeCallback,
      ...(shouldLaunchPinwheelSDK && {
        onLinkTokenError: handleDdManagementLinkTokenFailure,
        input: {
          experience: PinwheelExperienceInput.DirectDepositSwitch,
          skipIntroScreen,
        },
        launchBehavior: skipIntroScreen
          ? { delayAfterTokenMs: 3000 }
          : undefined,
      }),
    });
  };

  const isWithDDSelected = selectedOption === DirectDepositOption.WITH_DD;
  const isWithoutDDSelected = selectedOption === DirectDepositOption.WITHOUT_DD;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={goHome} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isPinwheelSdkLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={false}
            isPinwheelLoading={isPinwheelSdkLoading}
          />
        }
        ErrorComponent={null}
      />

      <ScrollView contentContainerStyle={styles.scrollContent} bounces={false}>
        <View style={styles.headerSection}>
          <TitleXLText numberOfLines={0}>
            {translatedText('headline')}
          </TitleXLText>
          <BodyBaseText numberOfLines={0}>
            {translatedText('body')}
          </BodyBaseText>
        </View>

        <View style={styles.radiosSection} accessibilityRole="radiogroup">
          {!pinwheelFlowPageError ? (
            <Pressable
              style={[
                styles.radioCard,
                isWithDDSelected
                  ? styles.radioCardSelected
                  : styles.radioCardUnselected,
              ]}
              onPress={() => setPreferredOption(DirectDepositOption.WITH_DD)}
              accessibilityRole="radio"
              accessibilityState={{ checked: isWithDDSelected }}
              accessibilityLabel={translatedText('withDD.title')}
            >
              <View style={styles.radioCardContent}>
                <Tag
                  text={translatedText('withDD.tag')}
                  type={TagType.INFO_EMPHASIZED}
                  iconKey="SPARKLE_FILLED"
                />
                <View style={styles.radioTextContent}>
                  <BodyBaseBoldText numberOfLines={0}>
                    {translatedText('withDD.title')}
                  </BodyBaseBoldText>
                  <BodySmText numberOfLines={0}>
                    {translatedText('withDD.description', {
                      minDirectDepositAmount:
                        DD_PROMPT_MIN_DIRECT_DEPOSIT_AMOUNT,
                      maxAdvanceLimit: DD_PROMPT_MAX_ADVANCE_LIMIT,
                    })}
                  </BodySmText>
                </View>
              </View>
              <View style={styles.radioIndicatorContainer}>
                <View style={styles.radioCircle}>
                  {isWithDDSelected ? (
                    <View style={styles.radioInnerCircle} />
                  ) : null}
                </View>
              </View>
            </Pressable>
          ) : null}
          <Pressable
            style={[
              styles.radioCard,
              isWithoutDDSelected
                ? styles.radioCardSelected
                : styles.radioCardUnselected,
            ]}
            onPress={() => setPreferredOption(DirectDepositOption.WITHOUT_DD)}
            accessibilityRole="radio"
            accessibilityState={{ checked: isWithoutDDSelected }}
            accessibilityLabel={translatedText('withoutDD.title')}
          >
            <View style={styles.radioCardContent}>
              <View style={styles.radioTextContent}>
                <BodyBaseBoldText numberOfLines={0}>
                  {translatedText('withoutDD.title')}
                </BodyBaseBoldText>
                <BodySmText numberOfLines={0}>
                  {translatedText('withoutDD.description')}
                </BodySmText>
              </View>
            </View>
            <View style={styles.radioIndicatorContainer}>
              <View style={styles.radioCircle}>
                {isWithoutDDSelected ? (
                  <View style={styles.radioInnerCircle} />
                ) : null}
              </View>
            </View>
          </Pressable>
          <BodyXSText numberOfLines={0} style={styles.disclaimer}>
            {translatedText('disclaimer')}
          </BodyXSText>
        </View>
      </ScrollView>

      <View
        style={[
          styles.buttonContainer,
          { paddingBottom: bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={translatedText('button')}
          onPress={handleGetAdvance}
          type="PRIMARY"
          size="LARGE"
          width="FULL"
          analyticsId={
            isWithDDSelected ? 'ddBorrowMoreWithDD' : 'ddBorrowMoreWithoutDD'
          }
          customAttributes={{
            cta: isWithDDSelected ? CTA_WITH_DD : CTA_WITHOUT_DD,
            pinwheel_error: pinwheelErrorAttribute,
          }}
          isDisabled={isPinwheelSdkLoading || pinwheelFlowDecisionLoading}
          isLoading={isPinwheelSdkLoading || pinwheelFlowDecisionLoading}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primarySolid,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  disclaimer: {
    color: theme.color.text.secondary,
  },
  headerSection: {
    gap: theme.spacing.s,
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  radioCard: {
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    padding: theme.spacing.m,
  },
  radioCardContent: {
    flex: 1,
    gap: theme.spacing.s,
  },
  radioCardSelected: {
    borderColor: theme.color.interactive.primary,
  },
  radioCardUnselected: {
    borderColor: theme.color.border.primarySolid,
  },
  radioCircle: {
    alignItems: 'center',
    borderColor: theme.color.interactive.primary,
    borderRadius: 12,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  radioIndicatorContainer: {
    paddingTop: 2,
  },
  radioInnerCircle: {
    backgroundColor: theme.color.interactive.primary,
    borderRadius: 8,
    height: 16,
    width: 16,
  },
  radioTextContent: {
    gap: theme.spacing.s,
  },
  radiosSection: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  scrollContent: {
    flexGrow: 1,
  },
});

export default TakeOutAdvanceDdPrompt;
